import UpIcon from '@Resources/assets/icons/dark/prevVertical.svg';
import ColorStyles from '@Styles/colors.scss';
import { CircularProgress, styled } from '@mui/material';
import _groupBy from 'lodash/groupBy';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import React from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import {
    allAttachedSpansState,
    allOtherSpansState,
    backSpanState,
    centrePoleState,
    foreSpanState,
    hasCentrePoleState,
    imageIndicesState,
} from '@Data/qualityControl/Reducer';
import QcSymbology from '@Views/QualityControl/styles/symbology.scss';
import { RESIZE_REFRESH_DELAY } from '../SvgViewer/constants';
import Styles from './PlanView.scss';
import { Anchor } from './components/Anchor';
import { CameraPosition } from './components/CameraPosition';
import { Compass } from './components/Compass';
import { Equipment } from './components/Equipment';
import { SpanLine, SpanType } from './components/SpanLine';

const PlanViewBackground = styled('circle')`
    fill: ${ColorStyles.ikeGradient11};
    stroke: ${ColorStyles.ikeGradient11};
`;

const SelectedPole = styled('circle')`
    fill: ${ColorStyles.ikeSecondaryLightBlue};
    stroke: ${ColorStyles.ikeSecondaryLightBlue};
`;

export interface ICircleObjectProps {
    bearing?: number;
    circleRadius: number;
    circleCenter: number;
}

const SELECTED_POLE_RADIUS = 8;
const SVG_PADDING = 20;

export const PlanView = () => {
    const { width, height, ref } = useResizeDetector({
        refreshMode: 'debounce',
        refreshRate: RESIZE_REFRESH_DELAY,
    });

    const selectedPole = useSelector(centrePoleState);
    const foreSpan = useSelector(foreSpanState);
    const backSpan = useSelector(backSpanState);
    const hasCentrePole = useSelector(hasCentrePoleState);
    const attachedSpans = useSelector(allOtherSpansState);
    const hasForeSpan = !_isNil(foreSpan) && !_isNil(foreSpan.bearing);
    const hasBackSpan = !_isNil(backSpan) && !_isNil(backSpan.bearing);
    const planViewId = 'planViewSvg';

    const visibleImageIndices = useSelector(imageIndicesState);
    const selectedImageIndex = visibleImageIndices[2];

    let SVG_DIMENSION = 0;
    let CIRCLE_RADIUS = 0;
    let CIRCLE_CENTER = 0;
    if (!_isNil(width) && !_isNil(height)) {
        SVG_DIMENSION = height < width ? height : width;

        CIRCLE_RADIUS = SVG_DIMENSION / 2 - SVG_PADDING;
        CIRCLE_CENTER = SVG_DIMENSION / 2;
    }

    let innerHtml = (
        <div className={Styles.loadingContainer}>
            <CircularProgress />
        </div>
    );

    const circleProps = {
        circleRadius: CIRCLE_RADIUS,
        circleCenter: CIRCLE_CENTER,
    };

    if (hasCentrePole) {
        const otherCameraPositions = selectedPole.truesizeImageInfo
            .filter((_, i) => i !== selectedImageIndex)
            .map((image) => (
                <CameraPosition
                    bearing={image.bearing}
                    {...circleProps}
                    key={`cameraImg${image.url}${Math.random()}`}
                />
            ));

        const selectedCameraPosition = selectedPole.truesizeImageInfo
            .filter((_, i) => i === selectedImageIndex)
            .map((image) => (
                <CameraPosition
                    bearing={image.bearing}
                    {...circleProps}
                    key={`cameraImg${image.url}${Math.random()}`}
                    isSelected
                />
            ));

        const groupedEquipment = _groupBy(selectedPole.equipment, 'type');
        let groupedEquipmentArray = Object.values(groupedEquipment);
        groupedEquipmentArray = _orderBy(
            groupedEquipmentArray,
            'length',
            'desc'
        );

        const equipmentGroupHtml = [];

        for (let i = groupedEquipmentArray.length - 1; i > -1; i -= 1) {
            const equipmentGroup: any = groupedEquipmentArray[i];
            equipmentGroup.forEach((equipmentPiece) => {
                equipmentGroupHtml.push(
                    <Equipment
                        bearing={equipmentPiece.bearing}
                        {...circleProps}
                        distanceFromCenter={15 + i * 10}
                        strokeColor={QcSymbology.equipment}
                        key={`${equipmentPiece.id}${Math.random()}`}
                    />
                );
            });
        }

        innerHtml = (
            <>
                <svg
                    style={{
                        width: SVG_DIMENSION,
                        height: SVG_DIMENSION,
                    }}
                >
                    <PlanViewBackground
                        id={planViewId}
                        cx={CIRCLE_CENTER}
                        cy={CIRCLE_CENTER}
                        r={CIRCLE_RADIUS}
                    />
                    <Compass {...circleProps} />
                    {hasForeSpan && (
                        <SpanLine
                            bearing={foreSpan.bearing}
                            {...circleProps}
                            type={SpanType.FORE}
                        />
                    )}
                    {hasBackSpan && (
                        <SpanLine
                            bearing={backSpan.bearing}
                            {...circleProps}
                            type={SpanType.BACK}
                        />
                    )}

                    {attachedSpans
                        .filter((i) => !_isNil(i.bearing))
                        .map((s) => (
                            <SpanLine
                                bearing={s.bearing}
                                {...circleProps}
                                type={
                                    s.type === 'Other' || s.type === 'Building'
                                        ? SpanType.DROP
                                        : SpanType.OTHER
                                }
                                key={s.span_id}
                            />
                        ))}

                    {otherCameraPositions}
                    {selectedCameraPosition}
                    {equipmentGroupHtml}

                    {!_isNil(selectedPole.anchors) &&
                        selectedPole.anchors.map((a) => (
                            <Anchor
                                bearing={a.bearing}
                                {...circleProps}
                                key={a.id}
                            />
                        ))}

                    <SelectedPole
                        cx={CIRCLE_CENTER}
                        cy={CIRCLE_CENTER}
                        r={SELECTED_POLE_RADIUS}
                    />
                </svg>
            </>
        );
    }

    return (
        <div className={Styles.planViewContainer} ref={ref}>
            {innerHtml}
        </div>
    );
};
